import { useState, useEffect, useCallback } from 'react'
import { IconRobot, IconUser, IconUndo } from '@/components/icons/Icons'
import { useRealtimeEvent } from '@/hooks/useRealtimeEvents'

/**
 * Reemplaza el cuadro de texto mientras un bot está atendiendo el chat.
 *
 * ─── Por qué BLOQUEA en vez de avisar al costado ───
 *
 * La versión anterior era una barra arriba del cuadro de texto: avisaba, pero dejaba
 * escribir. Eso es exactamente lo que no se puede permitir — una persona contesta en
 * medio de una charla del bot, el bot sigue, y al mensaje siguiente del cliente le
 * escriben los dos. El cliente recibe una pregunta del bot y una respuesta humana
 * cruzadas, y no sabe a cuál contestar.
 *
 * Bloquear no es quitarle el control a nadie: el botón está acá al lado, y además
 * escribir desde cualquier otro lado (el celular, el chat de Wazzup) también calla al
 * bot — eso lo resuelve el backend en `/chat/send`. Lo que el bloqueo evita es hacerlo
 * **sin darse cuenta**.
 *
 * ─── Y la vuelta ───
 *
 * Tomar el control corta la charla, no el chat. Para los bots de soporte, pedidos y estado
 * eso alcanza: su tabla admite una sesión nueva por cada cosa que el cliente reporta, así
 * que vuelven solos con lo siguiente que pida. El recepcionista no —tiene una fila por
 * conversación, porque a un desconocido se lo saluda una vez— y por eso es el único que
 * necesita el botón de `DevolverAlBot`: sin él se quedaba callado para siempre.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** Cómo se llama cada bot para quien mira el Inbox, que no piensa en slugs. */
const NOMBRES: Record<string, string> = {
  'soporte-whatsapp': 'El bot de soporte',
  'pedidos-whatsapp': 'El bot de pedidos',
  'estado-whatsapp': 'El bot de estado',
  'recepcionista-whatsapp': 'El recepcionista',
}

/** Qué está haciendo, por paso. Sin esto la barra dice «atendiendo» y no se sabe nada. */
const PASOS: Record<string, string> = {
  aviso: 'tomando nota del reporte',
  que_dice: 'preguntando qué pasa',
  quien: 'pidiendo el correo del afectado',
  captura: 'pidiendo una captura',
  donde: 'preguntando desde dónde',
  acuse: 'acusando recibo',
  confirmar: 'confirmando la fecha',
  saludo: 'pidiendo nombre y empresa',
  necesidad: 'preguntando qué necesita',
  agenda: 'ofreciendo horarios',
  cierre: 'cerrando',
}

export interface BotDelChat {
  origen: 'frontdesk' | 'recepcionista'
  agente: string
  paso: string
}

/** Que el chat se le puede devolver a su bot, y por qué había dejado de atender. */
export interface PuedeDevolverse {
  puede: boolean
  /** Cuál de los cuatro, para nombrarlo: «El bot de soporte», «El recepcionista». */
  agente?: string
  motivo_entrega?: string | null
}

/** Quién atiende este chat, y cómo cortarlo. Devuelve `null` mientras no haya ninguno. */
export function useBotDelChat(conversacionId: number | null) {
  const [bot, setBot] = useState<BotDelChat | null>(null)
  const [devolver, setDevolver] = useState<PuedeDevolverse | null>(null)
  const [tomando, setTomando] = useState(false)
  const [devolviendo, setDevolviendo] = useState(false)
  const [errorDevolver, setErrorDevolver] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    if (!conversacionId) { setBot(null); setDevolver(null); return }
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return
      const res = await fetch(`${API_URL}/api/chat/conversaciones/${conversacionId}/bot`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return
      const data = await res.json()
      setBot(data.bot ?? null)
      setDevolver(data.devolver ?? null)
    } catch {
      // Si falla, no se bloquea: ante la duda, que la persona pueda escribir.
      setBot(null)
      setDevolver(null)
    }
  }, [conversacionId])

  useEffect(() => { void cargar() }, [cargar])

  // El bot avanza en segundo plano: abre, pregunta y cierra sin que nadie recargue.
  useRealtimeEvent('new_message', cargar)

  const tomar = useCallback(async () => {
    if (!conversacionId) return
    setTomando(true)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/chat/conversaciones/${conversacionId}/bot/entregar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) { setBot(null); void cargar() }
    } finally {
      setTomando(false)
    }
  }, [conversacionId, cargar])

  // Devolverle el chat al recepcionista. Vuelve a preguntar en vez de asumir: el backend
  // puede decir que no (el chat se vinculó a un cliente mientras tanto) y ese no se
  // muestra tal cual — es un motivo que quien mira el chat entiende.
  const devolverElChat = useCallback(async () => {
    if (!conversacionId) return
    setDevolviendo(true)
    setErrorDevolver(null)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/chat/conversaciones/${conversacionId}/bot/devolver`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) setErrorDevolver(data?.error || 'No se pudo devolver el chat')
      await cargar()
    } finally {
      setDevolviendo(false)
    }
  }, [conversacionId, cargar])

  return { bot, tomar, tomando, devolver, devolverElChat, devolviendo, errorDevolver }
}

interface Props {
  bot: BotDelChat
  onTomar: () => void
  tomando: boolean
}

export default function BotAtendiendo({ bot, onTomar, tomando }: Props) {
  const nombre = NOMBRES[bot.agente] ?? 'Un bot'
  const que = PASOS[bot.paso]

  return (
    <div className="chat-bot-bloqueo">
      <IconRobot size={16} className="chat-bot-bloqueo__icono" />
      <span className="chat-bot-bloqueo__texto">
        <strong>{nombre} está atendiendo este chat</strong>
        {que ? ` — ${que}` : ''}
      </span>
      <button
        className="btn btn-sm btn-secondary chat-bot-bloqueo__tomar"
        onClick={onTomar}
        disabled={tomando}
        type="button"
        title="El bot deja de responder en este chat"
      >
        <IconUser size={13} /> {tomando ? 'Tomando…' : 'Tomar el control'}
      </button>
    </div>
  )
}

interface PropsDevolver {
  devolver: PuedeDevolverse
  onDevolver: () => void
  devolviendo: boolean
  error: string | null
}

/**
 * La vuelta: una línea **arriba** del cuadro de texto, que no lo bloquea.
 *
 * Al revés que `BotAtendiendo`, acá no hay nada que impedir — el chat es de la persona que
 * lo tomó y puede seguir escribiendo. Esto sólo le recuerda que el bot quedó parado ahí y
 * le da la forma de devolvérselo, que si no era un `UPDATE` a mano en la base.
 *
 * Nombra al bot porque los cuatro dejan la charla distinta: que vuelva el de soporte
 * significa que va a terminar de preguntar y dejar la tarea; que vuelva el recepcionista,
 * que va a seguir presentándose. No es lo mismo y quien decide tiene que saber cuál es.
 */
export function DevolverAlBot({ devolver, onDevolver, devolviendo, error }: PropsDevolver) {
  return (
    <div className="chat-bot-devolver">
      <IconRobot size={14} className="chat-bot-devolver__icono" />
      <span className="chat-bot-devolver__texto">
        {error
          ? error
          : <>{NOMBRES[devolver.agente ?? ''] ?? 'El bot'} dejó esta charla a medias{devolver.motivo_entrega ? ` — ${devolver.motivo_entrega}` : ''}</>}
      </span>
      <button
        className="btn btn-sm btn-ghost chat-bot-devolver__accion"
        onClick={onDevolver}
        disabled={devolviendo}
        type="button"
        title="Sigue en el paso donde quedó, con lo que el cliente ya le contó, y vuelve a preguntar lo que falta"
      >
        <IconUndo size={13} /> {devolviendo ? 'Devolviendo…' : 'Devolverle el chat'}
      </button>
    </div>
  )
}
